import { useState, useCallback, useMemo, useRef } from 'react'
import styled from '@emotion/styled'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import { CircularProgress, LinearProgress } from '@mui/material'
import { useDrop } from 'react-dnd'
import { useDispatch, useSelector, useStore } from 'react-redux'
import toast from 'react-hot-toast'
import { selectImageRefreshCounter } from 'slices/customer'
import { startCategorizing, finishCategorizing } from 'slices/global'
import { collectDroppedFileIds } from 'utils/helpers'
import Image from './Image'
import { Typography, Box, Tooltip } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import MaskedImage from 'components/MaskedImage'

const DragAndDropCard = ({
  text = 'Heading',
  height,
  position,
  children,
  borderColor,
  align,
  haveOcr,
  files = [],
  label,
  onDrop,
  isDeletable = false,
  deleteHandler,
  onCardDelete,
  allowMultiple = false,
  TextAdjustment = 'center',
  justifyContent = 'center',
  hasIconPurchase = false,
  isDocumentsLibrary = false,
  cardId = null,
  actualCardId = null,
  enableImagePaste = false,
  onPasteImage
}) => {
  const dispatch = useDispatch()
  const store = useStore()
  const imageRefreshCounter = useSelector(selectImageRefreshCounter)
  const [deleteLoader, setDeleteLoader] = useState(false)
  const [fileProgress, setFileProgress] = useState({})
  const pasteTargetRef = useRef(null)

  const isRemovedFile = useCallback(
    file => file?.is_removed === true || file?.file_obj?.is_removed === true,
    []
  )

  const getFileId = useCallback(file => {
    return (
      file?.file_obj?.id ||
      file?.file_obj?.document_revision_id ||
      file?.id ||
      file?.document_revision_id ||
      file?.document_id ||
      null
    )
  }, [])

  const dropHandler = useCallback(
    async item => {
      if (!item?.id?.file_obj?.id || deleteLoader) return
      if (!allowMultiple && files.length > 0) return

      const droppedFile = item?.id || item
      const fileId = getFileId(droppedFile)

      if (!fileId) return
      const alreadyDroppedIds = collectDroppedFileIds(store.getState())
      const droppedRevId =
        droppedFile?.file_obj?.document_revision_id ||
        droppedFile?.document_revision_id ||
        null
      const isAlreadyCategorized =
        alreadyDroppedIds.has(fileId) ||
        (droppedRevId && alreadyDroppedIds.has(droppedRevId))

      if (isAlreadyCategorized) {
        //toast.error('This document is already categorized in another section.')
        return
      }

      setFileProgress(prev => ({ ...prev, [fileId]: 0 }))
      dispatch(startCategorizing())

      let current = 0
      let progressInterval = null
      let isCompleted = false
      let maxTimeout = null

      const completeProgress = () => {
        if (isCompleted) return
        isCompleted = true
        dispatch(finishCategorizing())
        if (progressInterval) {
          clearTimeout(progressInterval)
          progressInterval = null
        }
        if (maxTimeout) {
          clearTimeout(maxTimeout)
          maxTimeout = null
        }

        setFileProgress(prev => ({ ...prev, [fileId]: 100 }))

        setTimeout(() => {
          setFileProgress(prev => {
            const updated = { ...prev }
            delete updated[fileId]
            return updated
          })
        }, 1000)
      }

      const incrementProgress = () => {
        if (isCompleted) return
        current += 1
        setFileProgress(prev => ({ ...prev, [fileId]: current }))
        if (current < 95) {
          progressInterval = setTimeout(incrementProgress, 50)
        } else if (current < 99) {
          progressInterval = setTimeout(incrementProgress, 150)
        }
      }
      incrementProgress()

      maxTimeout = setTimeout(() => {
        console.warn(`Progress timeout for file ${fileId}, forcing completion`)
        completeProgress()
      }, 30000)

      try {
        if (onDrop) {
          await onDrop(item?.id || item)
        }
        completeProgress()
      } catch (error) {
        console.error('Error during drop operation:', error)
        if (!isCompleted) {
          isCompleted = true
          dispatch(finishCategorizing())
        }
        if (progressInterval) {
          clearTimeout(progressInterval)
          progressInterval = null
        }
        if (maxTimeout) {
          clearTimeout(maxTimeout)
          maxTimeout = null
        }
        setFileProgress(prev => {
          const updated = { ...prev }
          delete updated[fileId]
          return updated
        })
      }
    },
    [allowMultiple, deleteLoader, files.length, onDrop, getFileId, dispatch, store]
  )

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: deleteLoader ? [] : 'image',
    drop: dropHandler,
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  })

  const isActive = isOver && canDrop

  const handleDelete = useCallback(
    async (index, file) => {
      if (!file) return
      setDeleteLoader(true)
      try {
        if (deleteHandler) await deleteHandler(index, file)
      } catch (e) {
        console.error('Error deleting file:', e)
      } finally {
        setDeleteLoader(false)
      }
    },
    [deleteHandler]
  )

  const handleCardDelete = useCallback(async () => {
    setDeleteLoader(true)
    try {
      if (files.length && deleteHandler) {
        await Promise.all(
          files.map((file, index) =>
            isRemovedFile(file) ? null : deleteHandler(index, file)
          )
        )
      }
      if (onCardDelete) await onCardDelete()
    } catch (e) {
      console.error('Error deleting card:', e)
    } finally {
      setDeleteLoader(false)
    }
  }, [files, deleteHandler, onCardDelete, isRemovedFile])

  const renderedFiles = useMemo(
    () =>
      files.map((file, index) => {
        const fileId = getFileId(file)
        const currentFileProgress = fileId ? fileProgress[fileId] : undefined
        const isFileUploading = currentFileProgress !== undefined

        const fileIsNewlyCategorized =
          file?.new_relation_addition || file?.file_obj?.new_relation_addition
        const lockThisFile = Boolean(cardId) && !fileIsNewlyCategorized
        const fileIsRemoved = isRemovedFile(file)

        return (
          <FileItem key={file.id || index}>
            <FileImageContainer>
              <FileImageBox>
                {lockThisFile ? (
                  <MaskedImage
                    item={file}
                    url={
                      file?.file_obj?.document_revision_id ||
                      file?.document_revision_id
                    }
                    alt={file?.file_obj?.file_name ?? file?.name}
                    width={'100%'}
                    height={'100%'}
                    cardId={cardId}
                    imageIndex={index}
                    refreshKey={imageRefreshCounter > 0 ? imageRefreshCounter : null}
                  />
                ) : (
                  <Image
                    item={file}
                    url={
                      file?.file_obj?.document_revision_id ||
                      file?.document_revision_id
                    }
                    alt={file?.file_obj?.file_name ?? file?.name}
                    dragfalse
                    width={'100%'}
                    imageIndex={index}
                    height={'100%'}
                    cardId={actualCardId || cardId}
                    shouldMask={
                      file?.gb_doc_type === 'credit_card_image' ||
                      file?.file_obj?.gb_doc_type === 'credit_card_image'
                    }
                    refreshKey={imageRefreshCounter > 0 ? imageRefreshCounter : null}
                  />
                )}
              </FileImageBox>
            </FileImageContainer>
            <FileDetails>
              <FileNameContainer>
                <FileName>
                  {file?.file_obj?.name ||
                    file?.file_obj?.file_name ||
                    file?.name ||
                    file?.fileName}
                </FileName>
                {!fileIsRemoved && (
                  <DeleteButton onClick={() => handleDelete(index, file)}>
                    <ClearIcon fontSize="small" />
                  </DeleteButton>
                )}
              </FileNameContainer>
              {isFileUploading ? (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={currentFileProgress}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ float: 'right', mt: 0.5 }}
                  >
                    {currentFileProgress}%
                  </Typography>
                </Box>
              ) : null}
            </FileDetails>
          </FileItem>
        )
      }),
    [
      files,
      cardId,
      actualCardId,
      handleDelete,
      fileProgress,
      getFileId,
      isRemovedFile,
      imageRefreshCounter
    ]
  )

  const hasActiveProgress = Object.keys(fileProgress).length > 0

  const handlePasteImage = useCallback(
    async event => {
      if (!enableImagePaste || typeof onPasteImage !== 'function') return
      if (!allowMultiple && files.length > 0) return
      await onPasteImage(event)
    },
    [enableImagePaste, onPasteImage, allowMultiple, files.length]
  )

  const focusPasteTarget = useCallback(() => {
    if (pasteTargetRef.current) pasteTargetRef.current.focus()
  }, [])

  const renderPasteTarget = content => (
    <PasteTarget
      ref={pasteTargetRef}
      contentEditable
      suppressContentEditableWarning
      role="application"
      aria-label={`${label || 'Document'} slot. Paste an image`}
      onPaste={handlePasteImage}
      onMouseDown={focusPasteTarget}
      onContextMenu={focusPasteTarget}
      onBeforeInput={event => event.preventDefault()}
      onKeyDown={event => {
        const isPasteShortcut =
          (event.ctrlKey || event.metaKey) &&
          (event.code === 'KeyV' ||
            event.key === 'v' ||
            event.key === 'V')
        const isAllowedKey = [
          'Tab',
          'Escape',
          'Shift',
          'Control',
          'Meta',
          'Alt'
        ].includes(event.key)
        if (!isPasteShortcut && !isAllowedKey) {
          event.preventDefault()
        }
      }}
      onCut={event => event.preventDefault()}
      onCopy={event => event.preventDefault()}
      onDragStart={event => event.preventDefault()}
    >
      {content}
    </PasteTarget>
  )

  const canPasteIntoCard =
    enableImagePaste &&
    typeof onPasteImage === 'function' &&
    (allowMultiple || files.length === 0)

  return (
    <DragAndDropCardContainer
      height={height}
      position={position}
      ref={drop}
      isActive={isActive}
      progress={hasActiveProgress}
      deleteLoader={deleteLoader}
      title={
        canPasteIntoCard
          ? 'Drop an image from the library, or paste'
          : undefined
      }
    >
      {isDeletable && (
        <CardDeleteButton onClick={handleCardDelete} disabled={deleteLoader}>
          {deleteLoader ? (
            <CircularProgress size={16} color="error" />
          ) : hasIconPurchase ? (
            <DeleteIcon fontSize="small" />
          ) : (
            <ClearIcon fontSize="small" />
          )}
        </CardDeleteButton>
      )}
      <DragAndDropHeaderRow borderColor={borderColor} align={align}>
        <DragAndDropHeaderText
          TextAdjustment={TextAdjustment}
          justifyContent={justifyContent}
        >
          <Text>{text}</Text>
          {haveOcr && (
            <OcrBadge>
              OCR <CheckIcon sx={{ fontSize: 12 }} />
            </OcrBadge>
          )}
        </DragAndDropHeaderText>
      </DragAndDropHeaderRow>
      <DragAndDropContent
        isActive={isActive}
        isDocumentsLibrary={isDocumentsLibrary}
      >
        {deleteLoader ? (
          <LoadingOverlay>
            <CircularProgress size={24} />
            <LoadingText>Deleting {label || 'Item'}...</LoadingText>
          </LoadingOverlay>
        ) : files.length > 0 ? (
          <>{renderedFiles}</>
        ) : canPasteIntoCard ? (
          renderPasteTarget(children)
        ) : (
          <>{children}</>
        )}
      </DragAndDropContent>
    </DragAndDropCardContainer>
  )
}

const DragAndDropCardContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  overflow: auto;
  opacity: ${({ progress, deleteLoader }) =>
    progress || deleteLoader ? 0.5 : 1};
  pointer-events: ${({ progress, deleteLoader }) =>
    progress || deleteLoader ? 'none' : 'auto'};
  border: ${({ isActive }) => (isActive ? '2px dashed #666' : 'none')};
  transition: all 0.3s ease;
  position: ${({ position }) => position || 'relative'};
  outline: none;
`

const PasteTarget = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  caret-color: transparent;
  cursor: default;
`

const CardDeleteButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  pointer-events: auto;
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  &:hover {
    background-color: rgba(220, 53, 69, 0.1);
    color: #c82333;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const DragAndDropHeaderRow = styled.div`
  border-bottom: 1px solid
    ${({ theme, borderColor }) =>
    borderColor ? theme.colors[borderColor] : theme.colors.lightGrey};
  padding-top: 13px;
  margin: 0px 15px;
  text-align: ${({ align }) => (align ? align : 'left')};
  position: relative;
`

const DragAndDropContent = styled.div`
  border: ${({ isActive }) => (isActive ? '1px dashed #666' : 'none')};
  margin: 10px;
  border-radius: 4px;
  min-height: ${({ isDocumentsLibrary }) =>
    isDocumentsLibrary ? 'auto' : '60px'};
`

const DragAndDropHeaderText = styled.h5`
  text-align: ${({ TextAdjustment }) => TextAdjustment};
  display: flex;
  align-items: center;
  margin: 0;
  padding-bottom: 6px;
  justify-content: ${({ justifyContent }) => justifyContent};
`

const Text = styled.span`
  text-align: ${({ TextAdjustment }) => TextAdjustment};
  margin: 0;
  padding: 0;
`

const OcrBadge = styled.span`
  background-color: #32cd32;
  color: white;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  margin-left: 8px;
  padding: 4px 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
`

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 150px;
  background-color: rgba(255, 255, 255, 0.9);
`

const LoadingText = styled.p`
  margin-top: 8px;
  font-size: 12px;
  color: #666;
`

const FileItem = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 8px;
  border-radius: 4px;
  margin: 8px;
`

const FileImageContainer = styled.div`
  flex: 0 0 80px;
`

const FileImageBox = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
`

const FileImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
`

const FileDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const FileNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  justify-content: space-between;
  width: 100%;
`

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 2px;
  font-size: 13px;
  margin-top: 2px;

  &:hover {
    color: #c82333;
  }
`

const FileName = styled.p`
  margin: 0;
  font-size: 12px;
  word-break: break-word;
  flex: 1;
`

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const ProgressBar = styled.div`
  background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
  height: 15px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`

export default DragAndDropCard
